'use client'

import { ChevronUp, Gift } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { PromotionButtonBooking } from '@/components/promotion/promotionButtonBooking'
import { usePromotionPopup } from '@/hooks/usePromotionPopup'
import { cls } from '@/libs/utils'

interface PromotionPopupProps {
  branches: IBranch[]
  promotions: IPromotion[]
  locale: string
}

export function PromotionPopup({ branches, promotions, locale }: PromotionPopupProps) {
  const [isFolded, setIsFolded] = useState(false)
  const [isIPhone, setIsIPhone] = useState(false)
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false)

  const pathname = usePathname()
  const t = useTranslations()

  //branch detail page
  const currentBranchPublicPath = (() => {
    // Pattern: /[locale]/destination/[city]/[branch]/
    const pathSegments = pathname.split('/').filter(Boolean)

    if (pathSegments.length >= 4 && pathSegments[1] === 'destination') {
      // pathSegments: [locale, 'destination', city, branch, ...]
      return pathSegments[3]
    }

    return undefined
  })()

  const currentCitySlug = (() => {
    // Pattern: /[locale]/destination/[city]/
    const pathSegments = pathname.split('/').filter(Boolean)

    if (pathSegments.length === 3 && pathSegments[1] === 'destination') {
      // pathSegments: [locale, 'destination', city]
      return pathSegments[2]
    }

    return undefined
  })()

  const { isVisible, isClosing, latestPromotion, nearestBranch, handleClose, handleBookNow } =
    usePromotionPopup({
      branches,
      promotions,
      locale,
      delayMs: 500,
      currentBranchPublicPath,
      currentCitySlug
    })

  // promotion page
  const isPromotionPage = (() => {
    if (!pathname.includes('/promotion')) return false

    // Get the part after '/promotion'
    const afterPromotion = pathname.split('/promotion')[1]

    // If there's nothing after '/promotion' or just a slash, it's the promotion listing page
    if (!afterPromotion || afterPromotion === '/') return true

    // If it starts with '/' and has content, it's a promotion detail page
    if (afterPromotion.startsWith('/')) {
      const cleanAfterPromotion = afterPromotion.substring(1).replace(/\/$/, '')
      return cleanAfterPromotion.length > 0
    }

    return false
  })()

  // Pay page
  const isPayPage = (() => {
    if (!pathname.includes('/pay')) return false

    // Get the part after '/promotion'
    const afterPromotion = pathname.split('/pay')[1]

    // If there's nothing after '/promotion' or just a slash, it's the promotion listing page
    if (!afterPromotion || afterPromotion === '/') return true

    // If it starts with '/' and has content, it's a promotion detail page
    if (afterPromotion.startsWith('/')) {
      const cleanAfterPromotion = afterPromotion.substring(1).replace(/\/$/, '')
      return cleanAfterPromotion.length > 0
    }

    return false
  })()

  // Detect iPhone device
  useEffect(() => {
    const userAgent = navigator.userAgent.toLowerCase()
    const isIPhoneDevice = /iphone/.test(userAgent)
    setIsIPhone(isIPhoneDevice)
  }, [])

  // Load folded state from localStorage on component mount
  useEffect(() => {
    const savedFoldedState = localStorage.getItem('promotionPopupFolded')
    if (savedFoldedState === 'true') {
      setIsFolded(true)
    }
  }, [])

  // Save folded state to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('promotionPopupFolded', isFolded.toString())
  }, [isFolded])

  // Don't render anything if not visible, no promotion, or on promotion detail page
  if (!isVisible || !latestPromotion || isPromotionPage || isPayPage) {
    return null
  }

  // Handle fold/unfold toggle
  const handleToggleFold = () => {
    setIsFolded(!isFolded)
  }

  // Handle booking modal state changes
  const handleBookingStart = () => {
    setIsBookingModalOpen(true)
    handleBookNow()
  }

  const handleBookingEnd = () => {
    setIsBookingModalOpen(false)
  }

  const promotionConstraints = {
    branchIds: String(latestPromotion.PM_brachID || ''),
    minGuests: latestPromotion.mincust || 1,
    maxGuests:
      latestPromotion.maxcust && latestPromotion.maxcust >= (latestPromotion.mincust || 1)
        ? latestPromotion.maxcust
        : Math.max((latestPromotion.mincust || 1) * 2, 10),
    advancebooking: latestPromotion.advancebooking || 0,
    isBuyOneGetOne: latestPromotion.promotion_buyone === 1,
    expired: false,
    expireDate: undefined,
    availableDateTimes: undefined,
    promotionUrl: latestPromotion.url || '',
    promotionId: latestPromotion.promotion_id || 0,
    cs_promotion_id: latestPromotion.promotion_id || 0
  }

  return (
    <div
      className={cls(
        'fixed bottom-6 left-6 transform overflow-hidden rounded-2xl shadow-2xl transition-all duration-500 ease-out',
        isFolded ? 'hover:scale-110' : 'hover:shadow-3xl w-80 border border-orange-100 bg-white',
        isClosing ? 'translate-y-4 scale-95 opacity-0' : 'translate-y-0 scale-100 opacity-100',
        isBookingModalOpen ? 'z-[1000]' : 'z-[9999]'
      )}
      style={isFolded ? { width: 'auto', height: 'auto' } : undefined}>
      {/* Folded State - Just Icon */}
      {isFolded ? (
        <button
          onClick={handleToggleFold}
          className='relative flex items-center justify-center overflow-hidden rounded-full bg-gradient-to-r from-orange-500 to-amber-600 p-3 text-white shadow-lg transition-all duration-300 hover:from-orange-600 hover:to-amber-700 md:p-4'
          style={{
            WebkitTapHighlightColor: 'transparent',
            WebkitUserSelect: 'none',
            userSelect: 'none'
          }}
          aria-label='Expand promotion popup'
          type='button'>
          <Gift size={isIPhone ? 20 : 24} className='animate-bounce text-white' />
        </button>
      ) : (
        <>
          {/* Unfolded State - Full Popup */}
          {/* Fold Button */}
          <div className='absolute right-3 top-3 z-10'>
            <button
              onClick={handleToggleFold}
              className='flex h-8 w-8 items-center justify-center rounded-full bg-orange-600 bg-opacity-30 shadow-lg transition-all duration-200 hover:bg-opacity-50'
              style={{
                WebkitBackdropFilter: 'blur(4px)',
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(255, 101, 12, 0.88)',
                WebkitTapHighlightColor: 'transparent',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
              }}
              aria-label='Minimize promotion popup'
              type='button'>
              <ChevronUp
                size={16}
                className='text-white drop-shadow-sm'
                style={{ transform: 'rotate(180deg)' }}
              />
            </button>
          </div>

          {/* Picture Section */}
          <div className='relative overflow-hidden' style={{ height: '142px' }}>
            <img
              className='h-full w-full bg-gray-50 object-contain'
              src={latestPromotion.promotion_img}
              alt={latestPromotion.promotion_lhead}
              loading='lazy'
            />

            {/* Gradient Overlay for better text readability */}
            <div className='absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent'></div>

            {/* Latest Badge */}
            {/* <div className="absolute top-4 left-4">
              <span className="bg-gradient-to-r from-green-800 to-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow-lg animate-pulse">
                ✨ LATEST
              </span>
            </div> */}

            {/* Branch Info Badge (only if promotion belongs to this branch) */}
            {nearestBranch &&
              latestPromotion &&
              latestPromotion.PM_brachID &&
              String(latestPromotion.PM_brachID).includes(nearestBranch.id) && (
                <div className='absolute bottom-3 left-3'>
                  <span className='rounded-full bg-gray-600/50 px-2 py-1 text-xs font-medium text-white backdrop-blur-sm'>
                    📍 {nearestBranch.name}
                  </span>
                </div>
              )}
          </div>

          {/* Content Section */}
          <div className='space-y-3 bg-gradient-to-b from-white to-green-50/30 p-4'>
            {/* Name Head - Clickable */}
            <h3
              className='text-black-800 cursor-pointer text-lg font-bold leading-tight tracking-tight transition-colors duration-200 hover:text-orange-600'
              onClick={() => {
                window.open(`/${locale}/promotion/${latestPromotion.landing_page}`, '_blank')
              }}
              title='Click to view promotion details'
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (latestPromotion.landing_page) {
                    window.open(`/${locale}/promotion/${latestPromotion.landing_page}`, '_blank')
                  } else if (latestPromotion.url) {
                    window.open(latestPromotion.url, '_blank')
                  }
                }
              }}>
              {latestPromotion.promotion_rhead}
            </h3>

            {/* Promotion Lead */}
            <p className='line-clamp-3 text-xs leading-relaxed text-gray-700' dangerouslySetInnerHTML={{ __html: latestPromotion.word_gotpromotion }}>
            </p>

            {/* Conditional Button based on template_component */}
            {latestPromotion.template_component === 'template_eGift_Green' ||
              latestPromotion.template_component === 'template_eGift_Cart' ? (
              /* BUY NOW Button for eGift_Green template */
              <button
                onClick={() => {
                  window.open(`/${locale}/promotion/${latestPromotion.landing_page}`, '_blank')
                }}
                className={`${isIPhone ? 'mx-auto w-4/5' : 'w-full'
                  } rounded-lg bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 px-4 py-2 text-center text-sm font-bold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-orange-600 hover:via-orange-700 hover:to-amber-700 active:scale-95`}
                style={{
                  WebkitTapHighlightColor: 'transparent',
                  WebkitUserSelect: 'none',
                  userSelect: 'none'
                }}
                type='button'>
                {t('eGift : Home Page.GIFTCERTIFICATE_buy_now').toUpperCase()}
              </button>
            ) : (
              /* BOOK Button for other templates */
              <PromotionButtonBooking
                constraints={promotionConstraints}
                onBookingStart={handleBookingStart}
                onBookingEnd={handleBookingEnd}
                enhanceWithDetailedData={true}
                promotionSlug={latestPromotion.landing_page}
                id='popup-promotion-booking-button'
                source='popup'
                className={`${isIPhone ? 'mx-auto w-4/5' : 'w-full'
                  } rounded-lg bg-gradient-to-r from-orange-500 via-orange-600 to-amber-600 px-4 py-2 text-center text-sm font-bold text-white shadow-lg transition-all duration-300 hover:scale-105 hover:from-orange-600 hover:via-orange-700 hover:to-amber-700 active:scale-95`}
              />
            )}
          </div>
          <div className='h-1 bg-gradient-to-r from-gray-600 via-gray-700 to-green-800'></div>
        </>
      )}
    </div>
  )
}
